import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CONNECTOR_NOTE_MAX, POINT_LABEL_MAX, STALE_AFTER_HOURS_MAX, STALE_AFTER_HOURS_MIN, newId,
  type ConnectorKindView, type ConnectorStatus, type ConnectorUpdateV1, type ConnectorView, type IngestionRunView,
} from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import { parsePeriod, periodPieces } from '../data/retention.ts'
import { useForecast } from '../data/weatherForecast.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, DisabledHint, Field, NotAllowed, PendingBanner, Pill, errorText } from '../ui/components.tsx'
import { useWorkshopAction } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'
import { RowLink } from '../ui/RowLink.tsx'
import { distanceText, outcomeText } from './Forecast.tsx'

/** M24-W, Réglages › Prévision météo (docs/M24.md "Screens"; A, E; rule 3, 5; ADR-125): the direction's page (connector.manage; NotAllowed otherwise, never the
 *  shared workstation), online, read through `api.connector_status` (SECURITY DEFINER), the file's own point name and the distance through `api.forecast_view`.
 *  The site's connector — its kind, the point's code, the club's name for it, the file's own name and its distance —, on / off and every change through
 *  `useWorkshopAction` (an office intent: `sendAndWait`, no projector; NO second-factor code since the owner's decision of 2026-10-28,
 *  `20261228100000_forecast_settings_no_code.sql`; a command kept on the device by a lost network says so with `PendingBanner` where the error shows, and
 *  every control that would send the same expected revision again holds until the page reads the connector again, which it does once no connector command
 *  waits in the device's outbox any more: M23), the staleness
 *  ("12 h, à confirmer" while it is the kind's default), the last 20 attempts of the server (when, the outcome, the HTTP status, a code in words: never a
 *  message) with the Retry-After cap, the keep of the attempts and original files (the club's `connector_log` policy, "à confirmer" until validated), the
 *  attribution and the licence. "Ajouter" when the site has no open connector; its hint says where the point codes are listed, with 06590 and P0276 as
 *  EXAMPLES: which point the club uses is its own choice, written nowhere in the code. The three kill switches are the server's: the page says so. */
export function ForecastSettingsPage() {
  const { t, i18n } = useTranslation()
  const { org, site, can, kiosk, db } = useClub()
  const allowed = can('connector.manage') && !kiosk
  const status = useRpc<ConnectorStatus>('connector_status', { p_org: org.org_id, p_site: site.site_id }, allowed)
  const forecast = useForecast(allowed && can('forecast.read'))
  // one action for the page, as the second-factor proof was: its answer shows under every control (the define's "added" under the new connector's, the
  // archive's "archived" under the form that follows); while a command waits on the device, every control holds (each would send the revision it read)
  const w = useWorkshopAction()
  const st = status.data
  const seen = st ? st.connectors.map((c) => `${c.connector_id}:${c.revision}`).join(',') : ''
  useEffect(() => { if (w.pending) w.reset() }, [seen])                                                                  // eslint-disable-line react-hooks/exhaustive-deps
  // the page reads the server only (no mirror whose next pull would let go of the hold, as on the fonctions' page): while a command waits, the device's
  // outbox is watched, and once no connector command waits there any more (applied, refused or discarded) the page reads the connector again and lets go.
  // The live count only wakes the check: the outbox is counted again at that moment, so a count the live query has not refreshed yet never lets go early
  const waitingConnector = () => db.outbox.where('status').anyOf(['queued', 'sending', 'transient', 'blocked']).filter((r) => r.name.startsWith('connector.')).count()
  const waiting = useLiveQuery(waitingConnector, [db])
  useEffect(() => {
    if (!w.pending) return
    let live = true
    void waitingConnector().then(async (n) => { if (n === 0 && live) { await status.reload(); if (live) w.reset() } })
    return () => { live = false }
  }, [w.pending, waiting])                                                                                                // eslint-disable-line react-hooks/exhaustive-deps
  if (!allowed) return <NotAllowed permission="connector.manage" />
  const open = st ? st.connectors.filter((c) => !c.archived_at) : []
  const archived = st ? st.connectors.filter((c) => c.archived_at) : []
  const offline = !status.online || status.error === 'OFFLINE'
  const reload = async () => { await status.reload(); await forecast.reload() }
  return (
    <section className="flex flex-col gap-5" data-testid="forecast-settings-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="weather" className="size-7 text-brand dark:text-fairway" />{t('forecast.title')}</h1>
      {offline && <Banner tone="warn"><span data-testid="fs-offline">{t('forecast.needsNetwork')}</span></Banner>}
      <p className="meta">{t('forecast.settings.help')}</p>
      {status.error && status.error !== 'OFFLINE' && <Banner tone="error"><span data-testid="fs-error">{errorText(t, status.error)}</span></Banner>}
      {st && open.map((c) => <ConnectorCard key={c.connector_id} c={c} st={st} w={w} fileName={forecast.view?.connector.connector_id === c.connector_id ? { name: forecast.view.issue.point_name, distance: distanceText(forecast.view, i18n.language, t) } : null} onDone={reload} />)}
      {st && open.length === 0 && (
        <>
          <p className="card" data-testid="fs-none">{t('forecast.settings.none')}</p>
          {st.kinds.length > 0 && <DefineForm kinds={st.kinds} w={w} onDone={reload} />}
        </>
      )}
      {archived.length > 0 && (
        <details className="card" data-testid="fs-archived">
          <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('forecast.settings.archivedList')}</summary>
          <ul className="meta">{archived.map((c) => <li key={c.connector_id}>{c.point_label} · {c.external_ref} · {formatLocal(c.archived_at!, site.timezone, 'dd/MM/yyyy')}</li>)}</ul>
        </details>
      )}
      {can('forecast.read') && <RowLink to="/forecast" icon="weather" tone="water" testId="fs-open-forecast" title={t('forecast.settings.openForecast')} />}
    </section>
  )
}

type Workshop = ReturnType<typeof useWorkshopAction>
/** A control and what the server answered about it — or, amber and never red, that the command waits on the device (M23's `PendingBanner`). */
function Acted({ w, children }: { w: Workshop; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3" data-testid="fs-acted">
      {children}
      {w.pending && <PendingBanner testId="fs-pending" />}
      {w.error && <Banner tone="error"><span data-testid="fs-action-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="fs-action-ok">{w.ok}</span></Banner>}
    </div>
  )
}

const words = (x: { fr: string; en: string }, lang: string) => (lang.startsWith('en') ? x.en : x.fr)
/** A whole number of hours within the bounds, '' for nothing typed, null for anything else. */
const staleValue = (raw: string): number | '' | null => {
  const s = raw.trim()
  if (s === '') return ''
  return /^\d{1,3}$/.test(s) && Number(s) >= STALE_AFTER_HOURS_MIN && Number(s) <= STALE_AFTER_HOURS_MAX ? Number(s) : null
}

function ConnectorCard({ c, st, w, fileName, onDone }: { c: ConnectorView; st: ConnectorStatus; w: Workshop; fileName: { name: string; distance: string } | null; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const kind = st.kinds.find((k) => k.code === c.kind)
  const isDefault = kind ? c.stale_after_hours === kind.default_stale_after_hours : false
  const keep = st.keep ? parsePeriod(st.keep.period) : null
  const keepText = keep ? periodPieces(keep).map((x) => t(`retention.unit.${x.unit}`, { count: x.count })).join(' ') : st.keep?.period ?? '—'
  const [label, setLabel] = useState(c.point_label)
  const [stale, setStale] = useState(String(c.stale_after_hours))
  const [note, setNote] = useState(c.note ?? '')
  const [nothing, setNothing] = useState(false)
  const staleV = staleValue(stale)
  const labelOk = label.trim().length >= 1 && label.trim().length <= POINT_LABEL_MAX
  const editBad = !labelOk || staleV === null || staleV === ''
  const held = w.busy || w.pending

  async function toggle() {
    const ok = await w.run('connector.update@1', { connector_id: c.connector_id, expected_revision: c.revision, enabled: !c.enabled }, c.enabled ? 'forecast.settings.offDone' : 'forecast.settings.onDone')
    if (ok) await onDone()
  }
  async function save() {
    const p: ConnectorUpdateV1 = { connector_id: c.connector_id, expected_revision: c.revision }
    if (label.trim() !== c.point_label) p.point_label = label.trim()
    if (typeof staleV === 'number' && staleV !== c.stale_after_hours) p.stale_after_hours = staleV
    const n = note.trim() === '' ? null : note.trim()
    if (n !== (c.note ?? null)) p.note = n
    if (Object.keys(p).length === 2) { setNothing(true); return }
    setNothing(false)
    if (await w.run('connector.update@1', p, 'forecast.settings.saved')) await onDone()
  }
  async function archive() {
    if (await w.run('connector.update@1', { connector_id: c.connector_id, expected_revision: c.revision, archive: true }, 'forecast.settings.archived')) await onDone()
  }
  return (
    <div className="flex flex-col gap-4" data-testid="fs-connector" data-connector={c.connector_id} data-enabled={c.enabled ? '1' : '0'}>
      <div className="card flex flex-col gap-1">
        <p className="meta" data-testid="fs-kind">{words(c.kind_label, lang)}</p>
        <p className="text-lg font-bold" data-testid="fs-label">{c.point_label}</p>
        <p data-testid="fs-code">{t('forecast.settings.code', { code: c.external_ref })}</p>
        <p className="meta" data-testid="fs-file">{fileName ? t('forecast.settings.fileName', fileName) : t('forecast.settings.fileNameUnknown')}</p>
        <p className="flex flex-wrap items-center gap-2" data-testid="fs-stale-line">
          <span data-testid="fs-stale">{t('forecast.settings.staleIs', { count: c.stale_after_hours })}{isDefault ? ` ${t('forecast.settings.toConfirm')}` : ''}</span>
          {c.freshness.stale && <Pill tone="gray">{t('forecast.stale')}</Pill>}
        </p>
        {c.note && <p className="meta">{c.note}</p>}
      </div>
      <div className="card flex flex-col gap-3">
        <p className="text-lg font-bold" data-testid="fs-state">{c.enabled ? t('forecast.settings.stateOn') : t('forecast.settings.stateOff')}</p>
        <Acted w={w}>
          <button type="button" className={`btn ${c.enabled ? '' : 'btn-primary'}`} disabled={held} onClick={() => void toggle()} data-testid="fs-toggle">
            {c.enabled ? t('forecast.settings.turnOff') : t('forecast.settings.turnOn')}
          </button>
        </Acted>
        <p className="meta" data-testid="fs-switches">{t('forecast.settings.switches')}</p>
      </div>
      <section className="flex flex-col gap-2" data-testid="fs-runs">
        <h2 className="text-xl font-bold">{t('forecast.settings.runs')}</h2>
        <p className="meta">{t('forecast.settings.runsHelp')}</p>
        {c.runs.length === 0 ? <p className="meta" data-testid="fs-no-runs">{t('forecast.settings.noRuns')}</p> : (
          <ul className="card flex flex-col divide-y divide-slate-200 !py-1 dark:divide-night-line">{c.runs.map((r) => <RunLine key={r.run_id} r={r} tz={site.timezone} />)}</ul>
        )}
        <p className="meta" data-testid="fs-retry-cap">{t('forecast.settings.retryCap', { count: st.retry_after_max_hours })}</p>
        <p className="meta" data-testid="fs-keep">{t('forecast.settings.keep', { period: keepText, confirm: st.keep && !st.keep.validated ? ` ${t('forecast.settings.toConfirm')}` : '' })}</p>
      </section>
      <div className="card flex flex-col gap-1">
        <p data-testid="fs-attribution">{t('forecast.settings.attribution', { attribution: words(c.attribution, lang), licence: c.licence ?? t('forecast.source.licenceNone') })}</p>
        {c.licence_url && <a href={c.licence_url} target="_blank" rel="noopener noreferrer" className="flex min-h-14 items-center break-all font-semibold text-brand underline underline-offset-2 dark:text-fairway" data-testid="fs-licence-link">{c.licence_url}</a>}
      </div>
      <details className="card" data-testid="fs-edit">
        <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('forecast.settings.edit')}</summary>
        <form className="mt-2 flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (!editBad) void save() }}>
          <Field label={t('forecast.settings.label')} hint={t('forecast.settings.labelHint')} htmlFor="fs-edit-label">
            <input id="fs-edit-label" className="input" maxLength={POINT_LABEL_MAX} value={label} onChange={(e) => { setLabel(e.target.value); setNothing(false) }} data-testid="fs-edit-label" />
          </Field>
          <Field label={t('forecast.settings.staleAfter')} hint={t('forecast.settings.staleHint', { count: kind?.default_stale_after_hours ?? 12 })} error={staleV === null ? t('forecast.settings.staleInvalid') : null} htmlFor="fs-edit-stale">
            <input id="fs-edit-stale" className="input" inputMode="numeric" value={stale} onChange={(e) => { setStale(e.target.value); setNothing(false) }} data-testid="fs-edit-stale" />
          </Field>
          <Field label={t('forecast.settings.note')} htmlFor="fs-edit-note">
            <textarea id="fs-edit-note" className="input min-h-20" maxLength={CONNECTOR_NOTE_MAX} rows={2} value={note} onChange={(e) => { setNote(e.target.value); setNothing(false) }} data-testid="fs-edit-note" />
          </Field>
          <Acted w={w}>
            <button className="btn btn-primary" disabled={held || editBad} data-testid="fs-edit-save">{t('forecast.settings.save')}</button>
            {nothing && <p className="meta" data-testid="fs-edit-nothing">{t('forecast.settings.nothing')}</p>}
          </Acted>
        </form>
      </details>
      <details className="card" data-testid="fs-archive-fold">
        <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('forecast.settings.archive')}</summary>
        <p className="meta">{t('forecast.settings.archiveHelp')}</p>
        <Acted w={w}>
          <button type="button" className="btn" disabled={held} onClick={() => void archive()} data-testid="fs-archive">{t('forecast.settings.archive')}</button>
        </Acted>
      </details>
    </div>
  )
}

function RunLine({ r, tz }: { r: IngestionRunView; tz: string }) {
  const { t } = useTranslation()
  const parts = [
    outcomeText(r.outcome, t),
    r.http_status !== null ? t('forecast.settings.httpStatus', { status: r.http_status }) : null,
    r.counts ? t('forecast.settings.counts', { ...r.counts }) : null,
    r.retry_after ? t('forecast.settings.retryAfter', { when: formatLocal(r.retry_after, tz) }) : null,
  ].filter(Boolean)
  return (
    <li className="flex flex-col gap-0.5 py-2" data-testid="fs-run" data-outcome={r.outcome}>
      <span className="font-semibold">{formatLocal(r.finished_at, tz)}</span>
      <span>{parts.join(' · ')}</span>
      {r.error_code && <span className="meta" data-code={r.error_code}>{errorText(t, r.error_code)}</span>}
    </li>
  )
}

function DefineForm({ kinds, w, onDone }: { kinds: ConnectorKindView[]; w: Workshop; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const [kind, setKind] = useState(kinds[0]!.code)
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [stale, setStale] = useState('')
  const [note, setNote] = useState('')
  const id = useRef(newId())                                              // one id per connector being defined: renewed only once applied (a command kept on the device keeps it)
  const k = kinds.find((x) => x.code === kind) ?? kinds[0]!
  const codeOk = new RegExp(k.external_ref_pattern).test(code.trim())
  const staleV = staleValue(stale)
  const labelOk = label.trim().length >= 1 && label.trim().length <= POINT_LABEL_MAX
  const bad = !codeOk || !labelOk || staleV === null
  async function define() {
    const ok = await w.run('connector.define@1', {
      connector_id: id.current, site_id: site.site_id, kind: k.code, external_ref: code.trim(), point_label: label.trim(),
      ...(typeof staleV === 'number' ? { stale_after_hours: staleV } : {}), ...(note.trim() ? { note: note.trim() } : {}),
    }, 'forecast.settings.defined')
    if (ok) { id.current = newId(); await onDone() }
  }
  return (
    <form className="card flex flex-col gap-3" data-testid="fs-define-form" onSubmit={(e) => { e.preventDefault(); if (!bad) void define() }}>
      <h2 className="text-xl font-bold">{t('forecast.settings.add')}</h2>
      <Field label={t('forecast.settings.kind')} htmlFor="fs-define-kind">
        <select id="fs-define-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="fs-define-kind">
          {kinds.map((x) => <option key={x.code} value={x.code}>{words(x.label, i18n.language)}</option>)}
        </select>
      </Field>
      <div className="flex flex-col gap-1">
        <Field label={t('forecast.settings.pointCode')} htmlFor="fs-define-code">
          <input id="fs-define-code" className="input" autoCapitalize="characters" autoComplete="off" spellCheck={false} value={code} onChange={(e) => setCode(e.target.value)} data-testid="fs-define-code" />
        </Field>
        {code.trim() && !codeOk && <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="fs-define-code-error">{t('forecast.settings.pointInvalid')}</p>}
        <p className="meta" data-testid="fs-define-hint">{t('forecast.settings.pointHint')}</p>
      </div>
      <Field label={t('forecast.settings.label')} hint={t('forecast.settings.labelHint')} htmlFor="fs-define-label">
        <input id="fs-define-label" className="input" maxLength={POINT_LABEL_MAX} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="fs-define-label" />
      </Field>
      <Field label={t('forecast.settings.staleAfter')} hint={t('forecast.settings.staleHint', { count: k.default_stale_after_hours })} error={staleV === null ? t('forecast.settings.staleInvalid') : null} htmlFor="fs-define-stale">
        <input id="fs-define-stale" className="input" inputMode="numeric" value={stale} onChange={(e) => setStale(e.target.value)} data-testid="fs-define-stale" />
      </Field>
      <Field label={t('forecast.settings.note')} htmlFor="fs-define-note">
        <textarea id="fs-define-note" className="input min-h-20" maxLength={CONNECTOR_NOTE_MAX} rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="fs-define-note" />
      </Field>
      <Acted w={w}>
        <button className="btn btn-primary" disabled={w.busy || w.pending || bad} data-testid="fs-define">{t('forecast.settings.define')}</button>
        {/* why the button waits (M23): a code or a name still to type; a wrong code or staleness says so under its own field */}
        <DisabledHint show={bad && !(code.trim() && !codeOk) && staleV !== null}>{!codeOk ? t('forecast.settings.needCode') : t('forecast.settings.needLabel')}</DisabledHint>
      </Acted>
    </form>
  )
}
